// REQ-P1-06b: 管理者チャット画面の契約型付きAPI。

import { api, type paths } from './client';
import type { operations } from './types.gen';
import { ApiStatusError } from './templates';

type MentionCandidatesResponse = NonNullable<
  paths['/admin/chat-mentions/admin-candidates']['get']['responses'][200]['content']
>['application/json'];
type PendingMentionTargetsQuery = NonNullable<
  paths['/admin/chat-mentions/pending']['get']['parameters']['query']
>;
type PendingMentionTargetsResponse = NonNullable<
  paths['/admin/chat-mentions/pending']['get']['responses'][200]['content']
>['application/json'];
type PendingMentionItemsQuery = NonNullable<
  paths['/admin/chat-mentions/pending-items']['get']['parameters']['query']
>;
type PendingMentionItemsResponse = NonNullable<
  paths['/admin/chat-mentions/pending-items']['get']['responses'][200]['content']
>['application/json'];
type ResolveMentionTargetRequest = NonNullable<
  paths['/admin/chat-mentions/resolve-target']['post']['requestBody']
>['content']['application/json'];
type ResolveMentionItemRequest = NonNullable<
  paths['/admin/chat-mentions/resolve-item']['post']['requestBody']
>['content']['application/json'];
type AdminMessagesResponse = NonNullable<
  paths['/admin/messages/{artistId}/{offset}/{limit}']['get']['responses'][200]['content']
>['application/json'];
type AdminMessagesWindowResponse = NonNullable<
  paths['/admin/messages/{artistId}/around/{messageId}/{before}/{after}']['get']['responses'][200]['content']
>['application/json'];
type AdminMessagesBeforeResponse = NonNullable<
  paths['/admin/messages/{artistId}/before/{beforeCreatedAt}/{limit}']['get']['responses'][200]['content']
>['application/json'];
type AdminNewMessagesResponse = NonNullable<
  paths['/admin/new-messages/{artistId}/{lastCreatedAt}']['get']['responses'][200]['content']
>['application/json'];
type AdminTextMessageRequest = NonNullable<
  paths['/admin/messages/text']['post']['requestBody']
>['content']['application/json'];
type AdminImageMessageUploadRequest = NonNullable<
  paths['/admin/messages/images']['post']['requestBody']
>['content']['multipart/form-data'];
type AdminFileMessageUploadRequest = NonNullable<
  paths['/admin/messages/files']['post']['requestBody']
>['content']['multipart/form-data'];
type AdminLatestMessageResponse = NonNullable<
  paths['/admin/messages/latest/{artistId}']['get']['responses'][200]['content']
>['application/json'];
type AdminUserForChatResponse = NonNullable<
  paths['/admin/user/{id}']['get']['responses'][200]['content']
>['application/json'];
type AdminNicknamePermissionsResponse = NonNullable<
  paths['/admin/nickname-permissions/{artistId}']['get']['responses'][200]['content']
>['application/json'];
type AdminArtistTargetRequest = NonNullable<
  paths['/admin/read-receipts']['post']['requestBody']
>['content']['application/json'];
type AdminReadReceiptSettingResponse = NonNullable<
  paths['/admin/read-receipts/{artistId}']['get']['responses'][200]['content']
>['application/json'];
type AdminArtistChatNotifyMuteResponse = NonNullable<
  paths['/admin/artist-chat/notify-mute/{artistId}']['get']['responses'][200]['content']
>['application/json'];
type UpdateAdminArtistChatNotifyMuteRequest = NonNullable<
  paths['/admin/artist-chat/notify-mute']['post']['requestBody']
>['content']['application/json'];
type AdminChatArtistsPagedQuery = NonNullable<
  paths['/admin/artists-with-parts/paged']['get']['parameters']['query']
>;
type AdminChatArtistsPagedResponse = NonNullable<
  paths['/admin/artists-with-parts/paged']['get']['responses'][200]['content']
>['application/json'];
type AdminChatArtistResponse = NonNullable<
  paths['/admin/artists-with-parts/one/{artistId}']['get']['responses'][200]['content']
>['application/json'];
type AdminPersonalUnreadCountResponse = NonNullable<
  paths['/admin/messages/unread-count']['get']['responses'][200]['content']
>['application/json'];
type AdminBulkEventCandidatesQuery = NonNullable<
  paths['/admin/messages/bulk/event-candidates']['get']['parameters']['query']
>;
type AdminBulkEventCandidatesResponse = NonNullable<
  paths['/admin/messages/bulk/event-candidates']['get']['responses'][200]['content']
>['application/json'];
type AdminBulkBatchMessageRequest = NonNullable<
  paths['/admin/messages/bulk/batch']['post']['requestBody']
>['content']['multipart/form-data'];
type AdminBulkMessageResult = NonNullable<
  paths['/admin/messages/bulk/batch']['post']['responses'][200]['content']
>['application/json'];
type EventCastingOverviewResponse = NonNullable<
  paths['/common/events/{eventId}/casting-overview']['get']['responses'][200]['content']
>['application/json'];
type StageSeatingParticipantArtistsResponse = NonNullable<
  paths['/admin/events/{eventId}/participant-artists']['get']['responses'][200]['content']
>['application/json'];
type AdminChatFollowupSummaryQuery = NonNullable<
  operations['getAdminChatFollowupSummary']['parameters']['query']
>;
type AdminChatFollowupSummaryResponse = NonNullable<
  operations['getAdminChatFollowupSummary']['responses'][200]['content']
>['application/json'];
type AdminChatFollowupsQuery = NonNullable<
  operations['listAdminChatFollowups']['parameters']['query']
>;
type AdminChatFollowupsResponse = NonNullable<
  operations['listAdminChatFollowups']['responses'][200]['content']
>['application/json'];
type CompleteAdminChatFollowupRequest = NonNullable<
  operations['completeAdminChatFollowup']['requestBody']
>['content']['application/json'];
type CompleteAdminChatFollowupResponse = NonNullable<
  operations['completeAdminChatFollowup']['responses'][200]['content']
>['application/json'];
type PromoteAdminChatFollowupRequest = NonNullable<
  operations['promoteAdminChatFollowup']['requestBody']
>['content']['application/json'];
type PromoteAdminChatFollowupResponse = NonNullable<
  operations['promoteAdminChatFollowup']['responses'][200]['content']
>['application/json'];

function ensureData<T>(data: T | undefined, status: number, ok: boolean): T {
  if (!ok || !data) throw new ApiStatusError(status);
  return data;
}

function ensureOk(status: number, ok: boolean) {
  if (!ok) throw new ApiStatusError(status);
}

async function ensureUploadOk(response: Response) {
  if (response.ok) return;
  const payload = await response.clone().json().catch(() => ({}));
  const message =
    payload && typeof payload === 'object'
      ? String(
          (payload as { error?: unknown; message?: unknown }).error ||
            (payload as { error?: unknown; message?: unknown }).message ||
            `HTTP ${response.status}`
        )
      : `HTTP ${response.status}`;
  throw new Error(message);
}

async function readOptionalJson(data: unknown, response: Response) {
  if (data && typeof data === 'object') return data as Record<string, unknown>;
  try {
    const json = await response.clone().json();
    return json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  } catch (_error) {
    return {};
  }
}

async function ensureOkWithPayloadMessage(response: Response, payload: Record<string, unknown>) {
  if (response.ok) return;
  throw new Error(String(payload?.message || payload?.error || `HTTP ${response.status}`));
}

function isAbortError(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && (error as { name?: unknown }).name === 'AbortError');
}

async function readJsonOrNull<T>(data: T | undefined, response: Response): Promise<T | null> {
  if (response.status === 404 || response.status === 403) return null;
  const payload = await readOptionalJson(data, response);
  if (!response.ok || payload?.success === false) return null;
  return payload as T;
}

export async function listAdminMentionCandidates(): Promise<MentionCandidatesResponse> {
  const { data, response } = await api.GET('/admin/chat-mentions/admin-candidates');
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as MentionCandidatesResponse;
}

export async function listAdminPendingMentionTargets(
  query: PendingMentionTargetsQuery
): Promise<PendingMentionTargetsResponse> {
  const { data, response } = await api.GET('/admin/chat-mentions/pending', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as PendingMentionTargetsResponse;
}

export async function listAdminPendingMentionItems(
  query: PendingMentionItemsQuery
): Promise<PendingMentionItemsResponse> {
  const { data, response } = await api.GET('/admin/chat-mentions/pending-items', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as PendingMentionItemsResponse;
}

export async function resolveAdminMentionTarget(body: ResolveMentionTargetRequest) {
  const { response } = await api.POST('/admin/chat-mentions/resolve-target', {
    body,
  });
  if (!response.ok) throw new ApiStatusError(response.status);
}

export async function resolveAdminMentionItem(body: ResolveMentionItemRequest) {
  const { response } = await api.POST('/admin/chat-mentions/resolve-item', {
    body,
  });
  if (!response.ok) throw new ApiStatusError(response.status);
}

export async function listAdminMessages(
  artistId: string,
  offset: number,
  limit: number,
  topicQuery: { topicId?: string } = {}
): Promise<AdminMessagesResponse> {
  const { data, response } = await api.GET('/admin/messages/{artistId}/{offset}/{limit}', {
    params: { path: { artistId, offset, limit }, query: topicQuery },
  });
  return ensureData(data as AdminMessagesResponse | undefined, response.status, response.ok);
}

export async function listAdminMessagesAround(
  artistId: string,
  messageId: string,
  before: number,
  after: number,
  topicQuery: { topicId?: string } = {}
): Promise<AdminMessagesWindowResponse> {
  const { data, response } = await api.GET(
    '/admin/messages/{artistId}/around/{messageId}/{before}/{after}',
    {
      params: { path: { artistId, messageId, before, after }, query: topicQuery },
    }
  );
  return ensureData(data as AdminMessagesWindowResponse | undefined, response.status, response.ok);
}

export async function listAdminMessagesBefore(
  artistId: string,
  beforeCreatedAt: string,
  limit: number,
  topicQuery: { topicId?: string } = {}
): Promise<AdminMessagesBeforeResponse> {
  const { data, response } = await api.GET(
    '/admin/messages/{artistId}/before/{beforeCreatedAt}/{limit}',
    {
      params: { path: { artistId, beforeCreatedAt, limit }, query: topicQuery },
    }
  );
  return ensureData(data as AdminMessagesBeforeResponse | undefined, response.status, response.ok);
}

export async function listAdminNewMessages(
  artistId: string,
  lastCreatedAt: string,
  topicQuery: { topicId?: string } = {}
): Promise<AdminNewMessagesResponse> {
  const { data, response } = await api.GET('/admin/new-messages/{artistId}/{lastCreatedAt}', {
    params: { path: { artistId, lastCreatedAt }, query: topicQuery },
  });
  return ensureData(data as AdminNewMessagesResponse | undefined, response.status, response.ok);
}

export async function sendAdminTextMessage(body: AdminTextMessageRequest, idempotencyKey?: string) {
  const { data, response } = await api.POST('/admin/messages/text', {
    body,
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
  const payload = await readOptionalJson(data, response);
  await ensureOkWithPayloadMessage(response, payload);
  return payload;
}

export async function sendAdminImageMessages(body: AdminImageMessageUploadRequest) {
  const { data, response } = await api.POST('/admin/messages/images', { body });
  await ensureUploadOk(response);
  return data;
}

export async function sendAdminFileMessage(body: AdminFileMessageUploadRequest) {
  const { data, response } = await api.POST('/admin/messages/files', { body });
  await ensureUploadOk(response);
  return data;
}

export async function getAdminChatFollowupSummary(
  query?: AdminChatFollowupSummaryQuery,
  options: { signal?: AbortSignal } = {}
): Promise<AdminChatFollowupSummaryResponse | null> {
  try {
    const { data, response } = await api.GET('/admin/chat-followups/summary', {
      params: query ? { query } : undefined,
      signal: options.signal,
    });
    return readJsonOrNull(data as AdminChatFollowupSummaryResponse | undefined, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    return null;
  }
}

export async function listAdminChatFollowups(
  query: AdminChatFollowupsQuery,
  options: { signal?: AbortSignal } = {}
): Promise<AdminChatFollowupsResponse | null> {
  try {
    const { data, response } = await api.GET('/admin/chat-followups', {
      params: { query },
      signal: options.signal,
    });
    return readJsonOrNull(data as AdminChatFollowupsResponse | undefined, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    return null;
  }
}

export async function completeAdminChatFollowup(
  body: CompleteAdminChatFollowupRequest
): Promise<CompleteAdminChatFollowupResponse> {
  const { data, response } = await api.POST('/admin/chat-followups/complete', { body });
  const payload = await readOptionalJson(data, response);
  if (!response.ok || payload?.success === false) {
    const error = new Error(String(payload?.message || '対応済み更新に失敗しました。'));
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }
  return payload as CompleteAdminChatFollowupResponse;
}

export async function promoteAdminChatFollowup(
  body: PromoteAdminChatFollowupRequest
): Promise<PromoteAdminChatFollowupResponse> {
  const { data, response } = await api.POST('/admin/chat-followups/promote', { body });
  const payload = await readOptionalJson(data, response);
  if (!response.ok || payload?.success === false) {
    const error = new Error(String(payload?.message || 'クイックタスク化に失敗しました。'));
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }
  return payload as PromoteAdminChatFollowupResponse;
}

export async function deleteAdminMessage(messageId: string): Promise<Record<string, unknown>> {
  const { data, response } = await api.DELETE('/admin/messages/{messageId}', {
    params: { path: { messageId } },
  });
  const payload = await readOptionalJson(data, response);
  await ensureOkWithPayloadMessage(response, payload);
  return payload;
}

export async function getLatestAdminMessage(artistId: string): Promise<AdminLatestMessageResponse> {
  const { data, response } = await api.GET('/admin/messages/latest/{artistId}', {
    params: { path: { artistId } },
  });
  return ensureData(data as AdminLatestMessageResponse | undefined, response.status, response.ok);
}

export async function getAdminUser(id: number): Promise<AdminUserForChatResponse> {
  const { data, response } = await api.GET('/admin/user/{id}', {
    params: { path: { id } },
  });
  return ensureData(data as AdminUserForChatResponse | undefined, response.status, response.ok);
}

export async function listAdminNicknamePermissions(
  artistId: string
): Promise<AdminNicknamePermissionsResponse> {
  const { data, response } = await api.GET('/admin/nickname-permissions/{artistId}', {
    params: { path: { artistId } },
  });
  return ensureData(data as AdminNicknamePermissionsResponse | undefined, response.status, response.ok);
}

export async function enableAdminNicknamePermission(body: AdminArtistTargetRequest) {
  const { response } = await api.POST('/admin/nickname-permission', { body });
  ensureOk(response.status, response.ok);
}

export async function disableAdminNicknamePermission(body: AdminArtistTargetRequest) {
  const { response } = await api.DELETE('/admin/nickname-permission', { body });
  ensureOk(response.status, response.ok);
}

export async function getAdminReadReceiptSetting(
  artistId: string
): Promise<AdminReadReceiptSettingResponse> {
  const { data, response } = await api.GET('/admin/read-receipts/{artistId}', {
    params: { path: { artistId } },
  });
  return ensureData(data as AdminReadReceiptSettingResponse | undefined, response.status, response.ok);
}

export async function enableAdminReadReceiptSetting(body: AdminArtistTargetRequest) {
  const { response } = await api.POST('/admin/read-receipts', { body });
  ensureOk(response.status, response.ok);
}

export async function disableAdminReadReceiptSetting(body: AdminArtistTargetRequest) {
  const { response } = await api.DELETE('/admin/read-receipts', { body });
  ensureOk(response.status, response.ok);
}

export async function getAdminArtistChatNotifyMute(
  artistId: string
): Promise<AdminArtistChatNotifyMuteResponse> {
  const { data, response } = await api.GET('/admin/artist-chat/notify-mute/{artistId}', {
    params: { path: { artistId } },
  });
  return ensureData(data as AdminArtistChatNotifyMuteResponse | undefined, response.status, response.ok);
}

export async function updateAdminArtistChatNotifyMute(
  body: UpdateAdminArtistChatNotifyMuteRequest
): Promise<AdminArtistChatNotifyMuteResponse> {
  const { data, response } = await api.POST('/admin/artist-chat/notify-mute', { body });
  return ensureData(data as AdminArtistChatNotifyMuteResponse | undefined, response.status, response.ok);
}

export async function markAdminPersonalMessagesRead(body: Record<string, unknown>) {
  const { response } = await api.POST('/admin/messages/is-read', { body });
  if (response.status === 409) return;
  ensureOk(response.status, response.ok);
}

export async function getAdminPersonalUnreadCount(): Promise<AdminPersonalUnreadCountResponse> {
  const { data, response } = await api.GET('/admin/messages/unread-count');
  return ensureData(data as AdminPersonalUnreadCountResponse | undefined, response.status, response.ok);
}

export async function searchAdminChatArtistsWithPartsPaged(
  query: AdminChatArtistsPagedQuery
): Promise<AdminChatArtistsPagedResponse> {
  const { data, response } = await api.GET('/admin/artists-with-parts/paged', {
    params: { query },
  });
  return ensureData(data as AdminChatArtistsPagedResponse | undefined, response.status, response.ok);
}

export async function getAdminChatArtistWithParts(
  artistId: string
): Promise<AdminChatArtistResponse> {
  const { data, response } = await api.GET('/admin/artists-with-parts/one/{artistId}', {
    params: { path: { artistId } },
  });
  return ensureData(data as AdminChatArtistResponse | undefined, response.status, response.ok);
}

export async function searchAdminBulkMessageEventCandidates(
  query: AdminBulkEventCandidatesQuery
): Promise<AdminBulkEventCandidatesResponse> {
  const { data, response } = await api.GET('/admin/messages/bulk/event-candidates', {
    params: { query },
  });
  return ensureData(data as AdminBulkEventCandidatesResponse | undefined, response.status, response.ok);
}

export async function listStageSeatingParticipantArtists(
  eventId: string
): Promise<StageSeatingParticipantArtistsResponse> {
  const { data, response } = await api.GET('/admin/events/{eventId}/participant-artists', {
    params: { path: { eventId } },
  });
  return ensureData(data as StageSeatingParticipantArtistsResponse | undefined, response.status, response.ok);
}

export async function getEventCastingOverview(
  eventId: string
): Promise<EventCastingOverviewResponse> {
  const { data, response } = await api.GET('/common/events/{eventId}/casting-overview', {
    params: { path: { eventId } },
  });
  return ensureData(data as EventCastingOverviewResponse | undefined, response.status, response.ok);
}

export async function sendAdminBulkBatchMessages(
  body: FormData,
  idempotencyKey: string
): Promise<AdminBulkMessageResult> {
  const { data, response } = await api.POST('/admin/messages/bulk/batch', {
    body: body as unknown as AdminBulkBatchMessageRequest,
    params: { header: { 'Idempotency-Key': idempotencyKey } },
  });
  const payload = await readOptionalJson(data, response);
  await ensureOkWithPayloadMessage(response, payload);
  return payload as AdminBulkMessageResult;
}
